/**
 * CoordinateValidationService
 *
 * Service for validating geographic coordinates and coordinate arrays.
 * Extracted from controllers to provide reusable validation logic.
 */

export interface Coordinate {
  lat: number;
  lng: number;
}

export class CoordinateValidationService {
  /**
   * Validates that a value is a valid coordinate number
   * @param value - The value to validate
   * @returns true if the value is a valid number
   */
  static isValidNumber(value: number | null | undefined): value is number {
    return value !== null && value !== undefined && !isNaN(value) && isFinite(value);
  }

  /**
   * Validates that a coordinate pair is valid
   * @param coord - The coordinate to validate
   * @returns true if the coordinate is valid
   */
  static isValidCoordinate(coord: Coordinate | null | undefined): coord is Coordinate {
    return coord !== null &&
           coord !== undefined &&
           this.isValidNumber(coord.lat) &&
           this.isValidNumber(coord.lng);
  }

  /**
   * Validates that a coordinate array is valid [lng, lat] format
   * @param coord - The coordinate array to validate
   * @returns true if the coordinate array is valid
   */
  static isValidCoordinateArray(coord: number[] | null | undefined): coord is [number, number] {
    return coord !== null &&
           coord !== undefined &&
           Array.isArray(coord) &&
           coord.length >= 2 &&
           this.isValidNumber(coord[0]) &&
           this.isValidNumber(coord[1]);
  }

  /**
   * Filters an array of coordinates to only include valid coordinates
   * @param coords - Array of coordinate arrays in [lng, lat] format
   * @returns Array of valid coordinates
   */
  static filterValidCoordinates(coords: number[][]): [number, number][] {
    return coords.filter((coord): coord is [number, number] =>
      this.isValidCoordinateArray(coord)
    );
  }

  /**
   * Converts coordinate array [lng, lat] to Coordinate object {lat, lng}
   * @param coord - Coordinate array in [lng, lat] format
   * @returns Coordinate object or null if invalid
   */
  static toCoordinateObject(coord: number[] | null | undefined): Coordinate | null {
    if (!this.isValidCoordinateArray(coord)) {
      return null;
    }
    return { lat: coord[1], lng: coord[0] };
  }
}

// Default export for convenience
export default CoordinateValidationService;
